import { Component } from '@angular/core';
import { Subject, switchMap, takeUntil, filter as rxFilter, lastValueFrom } from 'rxjs';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { WHInvBookkeepingService } from '../../services/w-h-inv-bookkeeping.service';
import { MenuItemId } from 'src/app/base/enum/menu-item-id.enum';
import { SimpleSearchComponent } from 'src/app/base/components/abstract/simple/simple-search.component';
import { Option } from 'src/app/base/models/option.model';
import { StockSummaryService } from '../../services/stock-summary.service';
import { CommonService } from '../../services/common.service';
import { Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';


@Component({
  selector: 'app-w-h-inv-bookkepping',
  templateUrl: './w-h-inv-bookkeeping.component.html',
  styleUrls: ['./w-h-inv-bookkeeping.component.scss'],
})
export class WHInvBookkeepingComponent extends SimpleSearchComponent {
  constructor(
    private wHInvBookkeepingService: WHInvBookkeepingService,
    private stockSummaryService: StockSummaryService,
    private commonService: CommonService,
    protected override dynamicDialogConfig: DynamicDialogConfig
  ) {
    super();
  }

  override rowDataKeyName: string = 'rowIndex';

  override rowDataDeleteHintFieldName: string = 'uuid';

  override componentClassName: string = 'WHInvBookkeepingComponent';

  override hasTableFilter: boolean = true;

  queryId: string = '';
  item?: string = '';
  ou?: number = 0;
  ouName?: string = '';
  beginMonth?: string = '';
  endMonth?: string = '';
  beginBalance?: number = 0;

  sales?: string = '';
  salesName?: string = '';
  cust?: string = '';
  requestor?: string = '';
  poNo?: string = '';
  poLineNo?: string = '';

  totalInQty?: string;
  totalOutQty?: string;
  selectedTransactionTypeName: string = '';

  ouSuggestions: Option[] = [];
  itemSuggestions: Option[] = [];

  private readonly ouSubject$ = new Subject<string | undefined>();
  private readonly itemSubject$ = new Subject<string | undefined>();

  qtyFilterMode: 'PARTIAL' | 'ALL' = 'PARTIAL';
  
  displayDatas: any[] = [];

  override initFormGroup(): void {
    // 當月
    const now = new Date();
    const endMonth = now.getFullYear().toString() + ('0' + (now.getMonth() + 1)).slice(-2);

    // 當月 - 2 個月
    const beginDate = new Date();
    beginDate.setMonth(beginDate.getMonth() - 2);
    const beginMonth = beginDate.getFullYear().toString() + ('0' + (beginDate.getMonth() + 1)).slice(-2);

    this.formGroup = this.formBuilder.nonNullable.group({
      ouFormControlName: [null, Validators.required],
      itemFormControlName: [null, Validators.required],
      beginMonthFormControlName: [beginMonth, Validators.required], //當月-2個月
      endMonthFormControlName: [endMonth], // 預設當月
    });
  }

  override initTableColumns(): void {
    this.tableColumns = [
      {
        header: 'field.trans-date',
        field: 'transDate',
        dateField: true,
        dateFormat: 'yyyy/MM/dd HH:mm:ss',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.delivery-so-iso-ad-pr-ir',
        field: 'deliverySoIsoAdPrIr',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
        width: 42,
      },
      {
        header: 'field.ar-no-inv-no',
        field: 'arNoInvNo',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.cust-name-vendor-name',
        field: 'customerNameVendorName',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.inter-corp',
        field: 'interCorp',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.in-qty',
        field: 'inQty',
        sortable: true,
        numberField: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.out-qty',
        field: 'outQty',
        sortable: true,
        numberField: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.balance',
        field: 'balance',
        sortable: true,
        numberField: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.transaction-type-name',
        field: 'transactionTypeName',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.trans-qty',
        field: 'transQty',
        sortable: true,
        numberField: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.org-name',
        field: 'orgName',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.sub-code',
        field: 'subCode',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.orig-sub-code',
        field: 'origSubCode',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.transaction-source-type-name',
        field: 'transactionSourceTypeName',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.meaning',
        field: 'meaningDisplay',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.po-price',
        field: 'poPrice',
        sortable: true,
        numberField: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.po-amt',
        field: 'poAmt',
        sortable: true,
        numberField: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.po-cur',
        field: 'poCurrency',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.so-price',
        field: 'soPrice',
        sortable: true,
        numberField: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.so-amt',
        field: 'soAmt',
        sortable: true,
        numberField: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
      {
        header: 'field.so-cur',
        field: 'soCurrency',
        sortable: true,
        filter: { type: 'text', matchMode: 'contains' },
      },
    ];
  }

  override async initFormData(): Promise<any> {
    this.loadingMaskService.show();
    await this.doQuery(this.dynamicDialogConfig?.data);
    return true;
  }

  get showSalesFields(): boolean {
    const type = (this.selectedTransactionTypeName ?? '').trim().toLowerCase();
    return ['sales order issue', 'int order intr ship'].includes(type);
  }

  get showCustFields(): boolean {
    const type = (this.selectedTransactionTypeName ?? '').trim().toLowerCase();
    return ['po receipt', 'int req intr rcpt'].includes(type);
  }

  get showPoFields(): boolean {
    const type = (this.selectedTransactionTypeName ?? '').trim().toLowerCase();
    return ['po receipt'].includes(type);
  }

  get showRequestorFields(): boolean {
    const type = (this.selectedTransactionTypeName ?? '').trim().toLowerCase();
    return ['po receipt', 'int req intr rcpt'].includes(type);
  }


  override onRowSelect(event?: any): void {
    const selectedRow = event.data; // 獲取選中的行資料
    if (selectedRow) {
      this.selectedTransactionTypeName = selectedRow.transactionTypeName;
      this.sales = selectedRow.empno;
      this.salesName = selectedRow.salesName;
      this.cust = selectedRow.cust;
      this.requestor = selectedRow.requestor;
      this.poNo = selectedRow.poNo;
      this.poLineNo = selectedRow.poLineNo;
      this.totalInQty = this.formatDisplayQty(selectedRow.inQty);
      this.totalOutQty = this.formatDisplayQty(selectedRow.outQty);
    }
  }

  override onRowUnselect(event?: any): void {
    this.sales = '';
    this.salesName = '';
    this.cust = '';
    this.requestor = '';
    this.poNo = '';
    this.poLineNo = '';
    this.totalInQty = '0';
    this.totalOutQty = '0';
  }

  override initExtraDetailMenu(): void {
    this.detailMenu = [];

    const automationId = this.tableFilterShow
      ? MenuItemId.I18N_FILTER_OFF   // Close Filter
      : MenuItemId.I18N_FILTER;      // Open Filter

    this.detailMenu.push({
      id: this.parseToId(MenuItemId.DETAIL_MENU_FILTER),
      label: this.translateService.instant(automationId),
      automationId,
      icon: this.tableFilterShow ? 'pi pi-fw pi-filter-slash' : 'pi pi-fw pi-filter',
      command: () => {
        this.clearSelectedData();
        this.detailMenuFilterCommand();
        setTimeout(() => {
          const source = this.tableFilterShow
            ? (this.tableComponent?.filteredValue ?? this.displayDatas ?? [])
            : (this.displayDatas ?? []);

          this.calculateTotalInOutQty(source);
        });
      },
    });

    // 新增的「Display Partial I/O History / Display All I/O History」切換按鈕
    this.detailMenu.push({
      id: this.parseToId('qtyFilterToggle'), // 自訂一個 ID
      label: this.qtyFilterMode === 'PARTIAL' ? 'Display Partial I/O History' : 'Display All I/O History',
      icon: this.qtyFilterMode === 'PARTIAL' ? 'pi pi-fw pi-eye-slash' : 'pi pi-fw pi-eye',
      command: () => {
        // 點擊時切換狀態
        this.qtyFilterMode = this.qtyFilterMode === 'PARTIAL' ? 'ALL' : 'PARTIAL';
        // 執行過濾計算
        this.applyQtyFilter();
        // 更新選單畫面狀態
        this.setExtraDetailMenuStatus();
      }
    });
  }

  /*
   ** 清除選取帶入的資料
   */
  clearSelectedData(): void {
    this.sales = '';
    this.salesName = '';
    this.cust = '';
    this.requestor = '';
    this.poNo = '';
    this.poLineNo = '';
  }

  searchOuSuggestions(event: { query: any }): void {
    if (!this.ouSubject$.observed) {
      this.ouSubject$
        .pipe(
          switchMap((keyword) => this.stockSummaryService.getOrgs(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => (this.ouSuggestions = data));
    }
    this.ouSubject$.next(event.query);
  }

  searchItemSuggestions(event: { query: any }): void {
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          rxFilter(Boolean),
          switchMap((keyword: string) => this.commonService.getItems(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => (this.itemSuggestions = data),
          error: () => (this.itemSuggestions = []),
        });
    }
    this.itemSubject$.next(event.query);
  }

  override async doQuery(requeryCondition?: any): Promise<boolean> {
    try {
      const uuid = requeryCondition?.uuid ?? '';
      if (!uuid) {
        return false;
      }

      const res = await lastValueFrom(
        this.wHInvBookkeepingService
          .getWHInvBookkeepingDetail({ uuid })
          .pipe(takeUntil(this._destroying$))
      );

      this.clearSelectedData();
      this.queryId = res?.queryId ?? '';

      const data = res?.responseData;

      // header
      this.item = data?.header?.item ?? '';
      this.ou = data?.header?.ou ?? 0;
      this.ouName = data?.header?.ouName ?? '';
      this.beginMonth = data?.header?.beginMonth ?? '';
      this.endMonth = data?.header?.endMonth ?? '';
      this.beginBalance = data?.header?.beginBalance ?? 0;

      // detail
      this.detailDatas = Array.isArray(data?.line)
        ? data.line.map((row: any, index: number) => ({
          ...row,
          inQty: row.inQty === 'null' || row.inQty == null ? '' : row.inQty,
          outQty: row.outQty === 'null' || row.outQty == null ? '' : row.outQty,
          balance: row.balance === 'null' || row.balance == null ? '' : row.balance,
          meaningDisplay: this.getMeaningByLang(row),
          rowIndex: index
        }))
        : [];

      // 預設套用Display Partial I/O History
      this.applyQtyFilter();

      // 預設選取第一筆資料，帶出 Sales 等相關資訊
      // 使用 setTimeout 確保 table 已完成渲染後再設定選取
      setTimeout(() => {
        if (this.displayDatas.length > 0) {
          const firstRow = this.displayDatas[0];
          this.rowSelectedDatas = firstRow;
          this.selectedTransactionTypeName = firstRow.transactionTypeName;
          this.sales = firstRow.empno;
          this.salesName = firstRow.salesName;
          this.cust = firstRow.cust;
          this.requestor = firstRow.requestor;
          this.poNo = firstRow.poNo;
          this.poLineNo = firstRow.poLineNo;
          this.totalInQty = this.formatDisplayQty(firstRow.inQty);
          this.totalOutQty = this.formatDisplayQty(firstRow.outQty);
        }
      });

      return true;
    } catch (e) {
      console.error(e);
      this.queryId = '';
      this.item = '';
      this.ou = 0;
      this.ouName = '';
      this.beginMonth = '';
      this.endMonth = '';
      this.beginBalance = 0;
      this.detailDatas = [];
      this.totalInQty = '0';
      this.totalOutQty = '0';
      return false;
    } finally {
      this.loadingMaskService.hide();
    }
  }

  override searchReset(): void {
    this.formGroup.reset();
    this.clearDetail();
    this.formGroup.patchValue(this.originalFormRawValue);
    this.submitted = false;
    this.resetItemInfo();
    this.clearSelectedData();
  }

  resetItemInfo(): void {
    this.queryId = '';
    this.item = '';
    this.ou = 0;
    this.ouName = '';
    this.beginMonth = '';
    this.endMonth = '';
    this.beginBalance = 0;
  }

  override setExtraDetailMenuStatus(): void {
    // 切換按鈕
    const toggleMenu = this.detailMenu.find((m) => m.id === this.parseToId('qtyFilterToggle'));

    if (toggleMenu) {
      // 根據當前的 qtyFilterMode 改變文字和圖示
      toggleMenu.label = this.qtyFilterMode === 'PARTIAL' ? 'Display Partial I/O History' : 'Display All I/O History';
      toggleMenu.icon = this.qtyFilterMode === 'PARTIAL' ? 'pi pi-fw pi-eye-slash' : 'pi pi-fw pi-eye';
    }

    // 重新賦值觸發 PrimeNG 畫面更新
    this.detailMenu = [...this.detailMenu];
  }

  override onDetailTableFilter(event: any): void {
    super.onDetailTableFilter(event);
    // 清除選取帶入的資料
    this.clearSelectedData();
    console.log("onDetailTableFilter=");

    // 重新計算合計數量
    this.calculateTotalInOutQty(this.tableComponent?.filteredValue ?? this.displayDatas ?? []);
  }

  calculateTotalInOutQty(source: any[]): void {
    if (!source || source.length === 0) {
      this.totalInQty = '';
      this.totalOutQty = '';
      return;
    }

    let totalIn = 0;
    let totalOut = 0;

    let hasInvalidIn = false;
    let hasInvalidOut = false;

    let hasAnyIn = false;
    let hasAnyOut = false;

    for (const row of source) {
      if (row.inQty !== '' && row.inQty !== null && row.inQty !== undefined && row.inQty !== 'null') {
        hasAnyIn = true;

        if (!this.isValidNumber(row.inQty)) {
          hasInvalidIn = true;
        } else {
          totalIn += Number(row.inQty);
        }
      }

      if (row.outQty !== '' && row.outQty !== null && row.outQty !== undefined && row.outQty !== 'null') {
        hasAnyOut = true;

        if (!this.isValidNumber(row.outQty)) {
          hasInvalidOut = true;
        } else {
          totalOut += Number(row.outQty);
        }
      }
    }

    this.totalInQty = !hasAnyIn
      ? ''
      : hasInvalidIn
        ? '*****'
        : this.formatDisplayQty(totalIn);

    this.totalOutQty = !hasAnyOut
      ? ''
      : hasInvalidOut
        ? '*****'
        : this.formatDisplayQty(totalOut);
  }

  private formatDisplayQty(value: any): string {
    if (value === '' || value === null || value === undefined || value === 'null') {
      return '';
    }

    if (isNaN(Number(value))) {
      return '*****';
    }

    return Intl.NumberFormat().format(Number(value));
  }

  applyQtyFilter(): void {

    if (this.qtyFilterMode === 'ALL') {
      this.displayDatas = [...this.detailDatas];
    } else {
      // 顯示inQty=0 或 outQty=0
      this.displayDatas = this.detailDatas.filter(row => row.hasQty);
    }

    // 搭配 table filter
    this.calculateTotalInOutQty(this.tableComponent?.filteredValue ?? this.displayDatas ?? []);
  }

  private isValidNumber(value: any): boolean {
    return value !== null
      && value !== undefined
      && value !== ''
      && !isNaN(Number(value));
  }

  getMeaningByLang(row: any): string {
    const lang = this.translateService.currentLang;
    console.log('lang=', lang);
    if (lang === 'en-us') return row.meaning ?? '';
    if (lang === 'zh-tw') return row.meaningZht ?? '';
    if (lang === 'zh-cn') return row.meaningZht ?? '';

    return row.meaningZht ?? row.meaning ?? '';

  }

  override afterLangChange(): void {
    this.detailDatas = (this.detailDatas || []).map((row: any) => ({
      ...row,
      meaningDisplay: this.getMeaningByLang(row)
    }));

    this.applyQtyFilter();
    this.setExtraDetailMenuStatus()
  }

  //以下未實作
  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    return;
  }

  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }

  override initDialogConfig(dataAction: DataAction): void {
    return;
  }

  override initExtraRowActionMenu(): void {
    return;
  }

  override setExtraRowActionMenuStatus(): void {
    return;
  }

  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }

  override afterDetailTabActiveItemChange(): void {
    return;
  }

  override initStaticOptions(): void {
    return;
  }

  override afterFormDataLoadSuccess(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  override initFormControlDisabled(): void {
    return;
  }

  override async doMasterMenuSubmit(): Promise<boolean> {
    return true;
  }

  override async doMasterMenuDelete(): Promise<boolean> {
    return true;
  }

  override initExtraMasterMenu(): void {
    return;
  }

  override setExtraMasterMenuStatus(): void {
    return;
  }

}